// src/pages/AdminEmpresaComprasList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Snackbar,
  Alert,
  Avatar,
  Divider,
  Tooltip,
  Grid,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PaidIcon from "@mui/icons-material/Paid";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import {
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../contexts/firebase/config";

function fmtYear(ts) {
  try {
    if (!ts) return "—";
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return isNaN(d) ? "—" : d.getFullYear();
  } catch {
    return "—";
  }
}

function fmtMoney(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toFixed(2);
}

function statusColor(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("abert")) return "default";
  if (s.includes("pend")) return "warning";
  if (s.includes("pago") || s.includes("concl")) return "success";
  if (s.includes("cancel")) return "error";
  return "default";
}

export default function AdminEmpresaComprasList() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { empresaId } = useParams();
  const navigate = useNavigate();

  const colors = {
    accent: theme.palette.primary.main,
    surface: isDark ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.78)",
    card: isDark ? "rgba(15,23,42,0.84)" : "rgba(255,255,255,0.84)",
    cardStrong: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: isDark ? "rgba(2,6,23,0.96)" : "rgba(15,23,42,0.96)",
  };

  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState("todos");

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    msg: "",
  });

  useEffect(() => {
    setLoading(true);

    const conditions = [where("empresaId", "==", empresaId)];

    if (filterYear !== "todos") {
      const year = parseInt(filterYear, 10);
      const start = Timestamp.fromDate(new Date(`${year}-01-01T00:00:00`));
      const end = Timestamp.fromDate(new Date(`${year}-12-31T23:59:59`));

      conditions.push(where("dataCompra", ">=", start));
      conditions.push(where("dataCompra", "<=", end));
    }

    const qRef = query(collection(db, "compras"), ...conditions);

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        setCompras(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [empresaId, filterYear]);

  const handleDeleteCompra = async (id) => {
    if (!window.confirm("Tem certeza de que deseja apagar esta compra?")) return;

    try {
      await deleteDoc(doc(db, "compras", id));
      setSnack({ open: true, severity: "success", msg: "Compra apagada." });
    } catch {
      setSnack({
        open: true,
        severity: "error",
        msg: "Não foi possível apagar a compra.",
      });
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/compra/${id}/edit`);
  };

  const totalValor = useMemo(
    () => compras.reduce((acc, c) => acc + (typeof c.valor === "number" ? c.valor : 0), 0),
    [compras]
  );

  const totalComissao = useMemo(
    () => compras.reduce((acc, c) => acc + (typeof c.comissao === "number" ? c.comissao : 0), 0),
    [compras]
  );

  const yearsFromData = useMemo(() => {
    const set = new Set();

    compras.forEach((c) => {
      const y = fmtYear(c.dataCompra);
      if (y && y !== "—") set.add(y);
    });

    const arr = Array.from(set).sort((a, b) => b - a);
    const nowY = new Date().getFullYear();

    [nowY, nowY - 1].forEach((y) => {
      if (!arr.includes(y)) arr.push(y);
    });

    return arr.sort((a, b) => b - a);
  }, [compras]);

  const selectSx = {
    borderRadius: "18px",
    bgcolor: colors.cardStrong,
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: alpha(colors.accent, 0.38) },
    "&.Mui-focused fieldset": { borderColor: colors.accent, borderWidth: 1 },
  };

  const StatCard = ({ icon, label, value, caption }) => (
    <Paper
      elevation={0}
      sx={{
        px: 1.6,
        py: 1.25,
        height: "100%",
        borderRadius: "26px",
        bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
        border: "1px solid rgba(255,255,255,0.18)",
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar
          sx={{
            width: 42,
            height: 42,
            borderRadius: "15px",
            bgcolor: "rgba(255,255,255,0.12)",
            color: "#ffffff",
            border: "1px solid rgba(255,255,255,0.14)",
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.66)", fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "1.15rem", fontWeight: 950, color: "#ffffff", lineHeight: 1.1 }}>
            {value}
          </Typography>
          {caption && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.52)" }}>
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(colors.accent, 0.12)}, transparent 58%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.16)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(colors.accent, 0.1)}, transparent 58%)`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.16 : 0.3,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 76%)",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: { xs: "28px", md: "42px" },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: "blur(22px)",
            WebkitBackdropFilter: "blur(22px)",
            boxShadow: isDark
              ? "0 28px 90px rgba(0,0,0,0.36)"
              : "0 24px 80px rgba(15,23,42,0.10)",
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              mb: 3,
              p: { xs: 2.5, md: 4 },
              borderRadius: "34px",
              bgcolor: colors.hero,
              color: "common.white",
              boxShadow: isDark
                ? "0 24px 70px rgba(0,0,0,0.34)"
                : "0 24px 70px rgba(15,23,42,0.22)",
              "&:before": {
                content: '""',
                position: "absolute",
                width: 560,
                height: 560,
                right: -150,
                top: -220,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.46)} 0%, ${alpha(colors.accent, 0.18)} 42%, transparent 70%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", xl: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "16px",
                      bgcolor: "rgba(255,255,255,0.12)",
                      color: "common.white",
                      border: "1px solid rgba(255,255,255,0.16)",
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon fontSize="small" />
                  </Avatar>

                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: "0.18em",
                      color: "rgba(255,255,255,0.66)",
                    }}
                  >
                    Empresa · Compras
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontSize: { xs: "2.15rem", sm: "2.85rem", md: "3.7rem" },
                    fontWeight: 950,
                    lineHeight: 0.98,
                    letterSpacing: "-0.07em",
                    color: "#ffffff",
                  }}
                >
                  Compras da Empresa
                </Typography>

                <Typography
                  sx={{
                    mt: 1.7,
                    maxWidth: 850,
                    color: "rgba(255,255,255,0.68)",
                    fontSize: { xs: "0.98rem", md: "1.08rem" },
                    lineHeight: 1.75,
                  }}
                >
                  Consulta compras, comissões e pagamentos associados a esta empresa.
                </Typography>
              </Box>

              <Grid container spacing={1.25} sx={{ width: { xs: "100%", xl: 485 }, flexShrink: 0 }}>
                <Grid item xs={12} sm={4} xl={12}>
                  <StatCard icon={<ShoppingBagRoundedIcon />} label="Registos" value={loading ? "…" : compras.length} />
                </Grid>
                <Grid item xs={12} sm={4} xl={12}>
                  <StatCard icon={<EuroRoundedIcon />} label="Total" value={`€ ${fmtMoney(totalValor)}`} />
                </Grid>
                <Grid item xs={12} sm={4} xl={12}>
                  <StatCard icon={<PaymentsRoundedIcon />} label="Comissões" value={`€ ${fmtMoney(totalComissao)}`} />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 2.25, md: 2.75 },
              borderRadius: "30px",
              border: `1px solid ${colors.border}`,
              bgcolor: colors.card,
              boxShadow: isDark ? "0 24px 64px rgba(0,0,0,0.24)" : "0 24px 60px rgba(15,23,42,0.07)",
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={1.45} alignItems="center">
                <Avatar
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "17px",
                    bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                    color: colors.accent,
                  }}
                >
                  <FilterAltRoundedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                    Filtros
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Filtra os registos por ano de compra.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                <Button
                  component={RouterLink}
                  to={`/admin/empresa/${empresaId}/info`}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  size="small"
                  sx={{
                    height: 40,
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 900,
                    bgcolor: colors.cardStrong,
                    borderColor: colors.border,
                  }}
                >
                  Voltar
                </Button>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Ano</InputLabel>
                  <Select
                    value={filterYear}
                    label="Ano"
                    onChange={(e) => setFilterYear(e.target.value)}
                    sx={{
                      borderRadius: "999px",
                      bgcolor: colors.cardStrong,
                      "& fieldset": { borderColor: colors.border },
                      "&:hover fieldset": { borderColor: alpha(colors.accent, 0.38) },
                      "&.Mui-focused fieldset": { borderColor: colors.accent, borderWidth: 1 },
                    }}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    {yearsFromData.map((y) => (
                      <MenuItem key={y} value={String(y)}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  component={RouterLink}
                  to={`/admin/empresa/${empresaId}/compras/new`}
                  variant="contained"
                  startIcon={<AddShoppingCartIcon />}
                  sx={{
                    height: 40,
                    borderRadius: "999px",
                    px: 2.1,
                    fontWeight: 950,
                    textTransform: "none",
                    boxShadow: "0 18px 38px rgba(37,99,235,0.24)",
                  }}
                >
                  Adicionar Compra
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {loading ? (
            <Paper
              elevation={0}
              sx={{
                p: 7,
                borderRadius: "30px",
                border: `1px solid ${colors.border}`,
                bgcolor: colors.card,
                textAlign: "center",
              }}
            >
              <CircularProgress />
            </Paper>
          ) : compras.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 7,
                textAlign: "center",
                borderRadius: "30px",
                border: `1px dashed ${colors.border}`,
                bgcolor: colors.card,
              }}
            >
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "26px",
                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                  color: colors.accent,
                }}
              >
                <ReceiptLongRoundedIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 950, color: colors.text }}>
                Não há compras registadas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Altera o filtro ou adiciona uma nova compra.
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "30px",
                border: `1px solid ${colors.border}`,
                overflow: "hidden",
                bgcolor: colors.card,
                boxShadow: isDark ? "0 24px 64px rgba(0,0,0,0.28)" : "0 24px 60px rgba(15,23,42,0.07)",
              }}
            >
              <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Stack direction="row" spacing={1.45} alignItems="center">
                    <Avatar
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "17px",
                        bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                        color: colors.accent,
                      }}
                    >
                      <ReceiptLongRoundedIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                        Lista de compras
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {compras.length} registo(s) encontrado(s).
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      label={`Total € ${fmtMoney(totalValor)}`}
                      sx={{
                        fontWeight: 900,
                        borderRadius: "999px",
                        bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                        color: colors.accent,
                      }}
                    />
                    <Chip
                      label={`Comissões € ${fmtMoney(totalComissao)}`}
                      sx={{
                        fontWeight: 900,
                        borderRadius: "999px",
                        bgcolor: alpha(theme.palette.success.main, isDark ? 0.18 : 0.1),
                        color: theme.palette.success.main,
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Divider sx={{ borderColor: colors.border }} />

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    minWidth: 980,
                    "& thead th": {
                      bgcolor: isDark ? "rgba(2,6,23,0.38)" : "rgba(248,250,252,0.92)",
                      color: colors.muted,
                      fontWeight: 950,
                      py: 1.7,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      borderBottom: `1px solid ${colors.border}`,
                    },
                    "& tbody tr": { transition: "0.18s ease" },
                    "& tbody tr:hover": { bgcolor: alpha(colors.accent, isDark ? 0.08 : 0.045) },
                    "& tbody td": { py: 1.55, borderBottom: `1px solid ${colors.border}` },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Ano da Compra</TableCell>
                      <TableCell align="right">Valor (€)</TableCell>
                      <TableCell align="right">Comissão (€)</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Ano do Pagamento</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {compras.map((c) => {
                      const anoCompra = fmtYear(c.dataCompra);
                      const anoPagamento = fmtYear(c.dataPagamento);
                      const color = statusColor(c.status);

                      return (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "15px",
                                  fontWeight: 950,
                                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                                  color: colors.accent,
                                }}
                              >
                                {c.clienteNome?.charAt(0)?.toUpperCase() || "C"}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 950, color: colors.text }} noWrap>
                                  {c.clienteNome || "—"}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              icon={<CalendarMonthRoundedIcon />}
                              label={anoCompra}
                              sx={{
                                fontWeight: 900,
                                borderRadius: "999px",
                                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
                                color: colors.text,
                                "& .MuiChip-icon": { color: colors.muted },
                              }}
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 950, color: colors.text }}>
                              € {fmtMoney(c.valor)}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Typography color="success.main" sx={{ fontWeight: 950 }}>
                              € {fmtMoney(c.comissao)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={c.status || "—"}
                              sx={{
                                fontWeight: 900,
                                borderRadius: "999px",
                                bgcolor:
                                  color === "warning"
                                    ? alpha(theme.palette.warning.main, isDark ? 0.2 : 0.12)
                                    : color === "success"
                                    ? alpha(theme.palette.success.main, isDark ? 0.18 : 0.1)
                                    : color === "error"
                                    ? alpha(theme.palette.error.main, isDark ? 0.18 : 0.1)
                                    : alpha(colors.accent, isDark ? 0.14 : 0.07),
                                color:
                                  color === "warning"
                                    ? theme.palette.warning.main
                                    : color === "success"
                                    ? theme.palette.success.main
                                    : color === "error"
                                    ? theme.palette.error.main
                                    : colors.accent,
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              icon={<EventRepeatRoundedIcon />}
                              label={anoPagamento}
                              sx={{
                                fontWeight: 900,
                                borderRadius: "999px",
                                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
                                color: colors.text,
                                "& .MuiChip-icon": { color: colors.muted },
                              }}
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                              {c.status === "Aberto" && (
                                <Tooltip title="Pagar">
                                  <IconButton
                                    component={RouterLink}
                                    to={`/admin/empresa/${empresaId}/compras/${c.id}/pagar`}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(colors.accent, isDark ? 0.14 : 0.08),
                                      color: colors.accent,
                                      "&:hover": { bgcolor: colors.accent, color: "#fff" },
                                    }}
                                  >
                                    <PaidIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Tooltip title="Editar">
                                <IconButton
                                  onClick={() => handleEdit(c.id)}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.warning.main, isDark ? 0.16 : 0.1),
                                    color: theme.palette.warning.main,
                                    "&:hover": { bgcolor: theme.palette.warning.main, color: "#fff" },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Apagar">
                                <IconButton
                                  onClick={() => handleDeleteCompra(c.id)}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.error.main, isDark ? 0.16 : 0.1),
                                    color: theme.palette.error.main,
                                    "&:hover": { bgcolor: theme.palette.error.main, color: "#fff" },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: "16px" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
